import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  FormField,
  Header,
  Input,
  Multiselect,
  MultiselectProps,
  Pagination,
  RadioGroup,
  SpaceBetween,
  Table,
  TableProps,
  TextFilter,
} from "@amzn/awsui-components-react";
import { OmsReport } from "../../lusca-search-api/generated-src";
import { columnPositionError, moveColumns } from "./omsColumnOrdering";

export type OmsColumnsDraft = {
  strategy: "all" | "selected";
  names: string[];
  touched?: boolean;
};

export function columnSelectionError(
  settings: OmsColumnsDraft,
  report: OmsReport
): string | undefined {
  if (settings.strategy === "all") return undefined;
  if (!settings.names.length) return "Choose at least one column.";
  const missing = settings.names.filter(
    (name) => !report.columns.includes(name)
  );
  if (missing.length)
    return `Unavailable in this source: ${missing.slice(0, 3).join(", ")}${
      missing.length > 3 ? ` and ${missing.length - 3} more` : ""
    }. Remove or replace these columns.`;
  if (new Set(settings.names).size !== settings.names.length)
    return "Remove duplicate output columns.";
  return undefined;
}

type Column = { name: string; position: number; type: string };
const PAGE_SIZE = 8;
const typeLabels = {
  string: "Text",
  number: "Number",
  date: "Date",
  datetime: "Date and time",
};

function tableColumns(total: number): TableProps.ColumnDefinition<Column>[] {
  return [
    {
      id: "position",
      header: "Position",
      width: 100,
      cell: (column) => column.position,
      editConfig: {
        ariaLabel: "Column position",
        editIconAriaLabel: "Edit column position",
        errorIconAriaLabel: "Invalid position",
        validation: (_column, value) => columnPositionError(value, total),
        editingCell: (column, context) => (
          <Input
            type="number"
            value={String(context.currentValue ?? column.position)}
            onChange={({ detail }) => context.setValue(detail.value)}
          />
        ),
      },
    },
    {
      id: "name",
      header: "Column",
      isRowHeader: true,
      cell: (column) => column.name,
    },
    { id: "type", header: "Type", width: 120, cell: (column) => column.type },
  ];
}

export default function OMSColumns({
  report,
  settings,
  onChange,
  focusRequested = false,
  onFocusHandled,
}: {
  report: OmsReport;
  settings: OmsColumnsDraft;
  onChange: (value: OmsColumnsDraft) => void;
  focusRequested?: boolean;
  onFocusHandled?: () => void;
}) {
  const picker = useRef<MultiselectProps.Ref>(null);
  const pickerContainer = useRef<HTMLDivElement>(null);
  const [openPicker, setOpenPicker] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selection, setSelection] = useState<string[]>([]);
  const [position, setPosition] = useState("1");
  const [moveAttempted, setMoveAttempted] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const error = columnSelectionError(settings, report);
  const visibleError =
    error && (settings.touched || settings.names.length > 0)
      ? error
      : undefined;
  useEffect(() => {
    if (!focusRequested && !openPicker) return undefined;
    const timer = window.setTimeout(() => {
      picker.current?.focus();
      const trigger = pickerContainer.current?.querySelector<HTMLButtonElement>(
        'button[aria-haspopup="dialog"], button[aria-haspopup="listbox"]'
      );
      if (openPicker && trigger?.getAttribute("aria-expanded") !== "true")
        trigger?.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
      setOpenPicker(false);
      onFocusHandled?.();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [focusRequested, openPicker, onFocusHandled]);

  const columns = settings.names.map((name, index) => ({
    name,
    position: index + 1,
    type: report.columns.includes(name)
      ? typeLabels[report.columnTypes?.[name] || "string"]
      : "Unavailable",
  }));
  const items = columns.filter((column) =>
    column.name.toLocaleLowerCase().includes(search.toLocaleLowerCase())
  );
  const selected = columns.filter((column) => selection.includes(column.name));
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const currentPage = Math.min(page, pages);
  const maxPosition = settings.names.length - selected.length + 1;
  const moveError = moveAttempted
    ? columnPositionError(position, maxPosition)
    : undefined;
  function changeNames(names: string[]) {
    onChange({ ...settings, names, touched: true });
  }
  function move(names: string[], nextPosition: number) {
    changeNames(moveColumns(settings.names, names, nextPosition));
    setAnnouncement(
      names.length === 1
        ? `Moved ${names[0]} to position ${nextPosition}.`
        : `Moved ${names.length} columns to position ${nextPosition}, keeping their relative order.`
    );
    setPage(Math.floor((nextPosition - 1) / PAGE_SIZE) + 1);
  }
  return (
    <div
      className={
        settings.strategy === "selected" && settings.names.length > 0
          ? "oms-columns__layout"
          : undefined
      }
    >
      <div className="oms-columns__selection">
        <SpaceBetween size="m">
          <FormField
            label="Columns to export"
            constraintText="All columns includes future fields. Selected columns keeps only your chosen fields."
          >
            <RadioGroup
              value={settings.strategy}
              items={[
                { value: "all", label: "All columns" },
                { value: "selected", label: "Selected columns" },
              ]}
              onChange={({ detail }) => {
                const strategy = detail.value as OmsColumnsDraft["strategy"];
                onChange({ ...settings, strategy });
                if (strategy === "selected") setOpenPicker(true);
              }}
            />
          </FormField>
          {settings.strategy === "all" && (
            <Box color="text-body-secondary">
              All {report.columns.length} columns in the report&apos;s original
              order.
            </Box>
          )}
          {settings.strategy === "selected" && (
            <>
              <FormField
                label="Column selection"
                errorText={visibleError}
                constraintText={
                  !visibleError && !settings.names.length
                    ? "Choose at least one column."
                    : undefined
                }
              >
                <div ref={pickerContainer}>
                  <Multiselect
                    ref={picker}
                    ariaLabel="Output columns"
                    filteringType="auto"
                    filteringPlaceholder="Search available columns"
                    filteringAriaLabel="Search available columns"
                    filteringClearAriaLabel="Clear available column search"
                    placeholder={
                      settings.names.length
                        ? "Add or remove columns"
                        : "Choose columns"
                    }
                    empty="This report has no available columns."
                    noMatch="No matching columns."
                    keepOpen
                    hideTokens
                    selectedOptions={settings.names.map((name) => ({
                      label: name,
                      value: name,
                    }))}
                    options={[...report.columns]
                      .sort((a, b) => a.localeCompare(b))
                      .map((name) => ({
                        label: name,
                        value: name,
                        description:
                          typeLabels[report.columnTypes?.[name] || "string"],
                      }))}
                    deselectAriaLabel={(option) =>
                      `Remove column ${
                        option.label || option.value || "column"
                      }`
                    }
                    onChange={({ detail }) =>
                      changeNames(
                        detail.selectedOptions.map(
                          (option) => option.value as string
                        )
                      )
                    }
                  />
                </div>
              </FormField>
              <SpaceBetween direction="horizontal" size="m">
                <Button
                  variant="inline-link"
                  onClick={() => changeNames([...report.columns])}
                >
                  Select all columns
                </Button>
                <Button
                  variant="inline-link"
                  disabled={!settings.names.length}
                  onClick={() => {
                    changeNames([]);
                    setSelection([]);
                  }}
                >
                  Clear column selection
                </Button>
              </SpaceBetween>
            </>
          )}
        </SpaceBetween>
      </div>
      {settings.strategy === "selected" && settings.names.length > 0 && (
        <SpaceBetween size="s">
          <Table<Column>
            variant="embedded"
            contentDensity="compact"
            stickyHeader
            wrapLines
            items={items.slice(
              (currentPage - 1) * PAGE_SIZE,
              currentPage * PAGE_SIZE
            )}
            trackBy="name"
            selectionType="multi"
            selectedItems={selected}
            onSelectionChange={({ detail }) => {
              setSelection(detail.selectedItems.map((column) => column.name));
              setMoveAttempted(false);
            }}
            ariaLabels={{
              tableLabel: "Selected CSV columns",
              selectionGroupLabel: "Columns to move or remove",
              allItemsSelectionLabel: () => "Select all columns on this page",
              itemSelectionLabel: (_state, column) => `Select ${column.name}`,
              activateEditLabel: (_column, item) =>
                `Change position of ${item.name}`,
              submitEditLabel: () => "Move column",
              cancelEditLabel: () => "Cancel position change",
              successfulEditLabel: () => "Column moved",
            }}
            header={
              <Header
                variant="h3"
                counter={`(${settings.names.length})`}
                description="Edit a position to move a column. Select several to move or remove them together."
                actions={
                  <Button
                    variant="inline-link"
                    onClick={() => {
                      changeNames(
                        [...settings.names].sort(
                          (a, b) =>
                            report.columns.indexOf(a) -
                            report.columns.indexOf(b)
                        )
                      );
                      setAnnouncement("Original report column order restored.");
                      setPage(1);
                    }}
                  >
                    Restore report order
                  </Button>
                }
              >
                CSV column order
              </Header>
            }
            filter={
              <SpaceBetween size="s">
                <TextFilter
                  filteringText={search}
                  filteringPlaceholder="Find selected columns"
                  filteringAriaLabel="Find selected columns"
                  filteringClearAriaLabel="Clear selected column search"
                  onChange={({ detail }) => {
                    setSearch(detail.filteringText);
                    setPage(1);
                  }}
                />
                {selected.length > 0 && (
                  <div className="oms-columns__bulk">
                    <Box fontWeight="bold">{selected.length} selected</Box>
                    <FormField label="Move to position" errorText={moveError}>
                      <Input
                        ariaLabel="Move selected columns to position"
                        type="number"
                        value={position}
                        onChange={({ detail }) => setPosition(detail.value)}
                      />
                    </FormField>
                    <Button
                      onClick={() => {
                        setMoveAttempted(true);
                        if (!columnPositionError(position, maxPosition))
                          move(
                            selected.map((column) => column.name),
                            Number(position)
                          );
                      }}
                    >
                      Move selected
                    </Button>
                    <Button
                      variant="inline-link"
                      onClick={() => {
                        const removing = selected.map((column) => column.name);
                        changeNames(
                          settings.names.filter(
                            (name) => !removing.includes(name)
                          )
                        );
                        setSelection([]);
                        setAnnouncement(
                          `Removed ${removing.length} columns from the export.`
                        );
                      }}
                    >
                      Remove selected
                    </Button>
                  </div>
                )}
              </SpaceBetween>
            }
            pagination={
              items.length > PAGE_SIZE ? (
                <Pagination
                  currentPageIndex={currentPage}
                  pagesCount={pages}
                  onChange={({ detail }) => setPage(detail.currentPageIndex)}
                  ariaLabels={{
                    nextPageLabel: "Next column page",
                    previousPageLabel: "Previous column page",
                    pageLabel: (index) => `Column page ${index}`,
                  }}
                />
              ) : undefined
            }
            empty={
              <Box textAlign="center" padding="s">
                No selected columns match your search.
              </Box>
            }
            columnDefinitions={tableColumns(settings.names.length)}
            submitEdit={(column, _definition, value) =>
              move([column.name], Number(value))
            }
          />
          {announcement && (
            <div role="status" aria-live="polite">
              <Box color="text-body-secondary">{announcement}</Box>
            </div>
          )}
        </SpaceBetween>
      )}
    </div>
  );
}
